/**
 * 與 auth-service 的 session 互動。
 *
 * 這裡沒有 token 的概念：access／refresh token 一律留在 auth-service 後端，
 * 瀏覽器只拿 HttpOnly 的 session cookie，JS 讀不到也偷不走。故所有請求都靠
 * `withCredentials` 帶上 cookie，而不是在 header 裡塞 Bearer。
 */

import { httpClient } from './api';
import { logoutUrl, meUrl, returnToParam } from './auth-config';
import { readCsrfToken } from './csrf';

/** 目前登入者。欄位對應 auth-service 的 `MeResponse`。 */
export interface AuthUser {
  /** Keycloak 的 sub（OIDC subject），跨服務識別使用者的唯一鍵 */
  readonly subject: string;
  readonly email: string | null;
  readonly displayName: string | null;
}

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

/**
 * 查詢目前登入者。
 *
 * 未登入時 auth-service 回 401（而非導向登入頁），故這裡把 401 視為正常結果
 * 回傳 `null`，只有真正的異常才拋錯 —— 「沒登入」不是錯誤，是兩種正常狀態之一。
 */
export async function fetchSession(signal?: AbortSignal): Promise<AuthUser | null> {
  /*
   * 自行判讀狀態碼而不交給 httpClient 的錯誤 interceptor：那裡會把 401 廣播成
   * 「session 過期」並把畫面換回登入，但這支端點的 401 只是「還沒登入」——
   * 每次開啟 app 的第一次查詢都會白白觸發一次過期通知。
   */
  const response = await httpClient.get<AuthUser>(meUrl(), {
    signal,
    validateStatus: () => true,
  });

  if (response.status === 401 || response.status === 403) {
    return null;
  }
  if (response.status >= 400) {
    throw new Error(`無法取得登入狀態（HTTP ${response.status}）`);
  }
  return response.data;
}

/**
 * 登出。
 *
 * auth-service 除了清掉自己的 session，還會把瀏覽器導去 Keycloak 結束 SSO session
 * （RP-Initiated Logout）。只清本站 session 的話，下次點登入會因為 Keycloak 那邊
 * 還記得你而直接登入成功，看起來像「登出根本沒生效」。
 *
 * 用**真正的表單送出**而非 fetch：登出是一連串跨源導向（本站 → auth-service →
 * Keycloak → 導回本站），需要瀏覽器逐步跟隨。fetch 在 `redirect: 'manual'` 下
 * 只會拿到 opaqueredirect（讀不到 Location），在 `follow` 下則會把導向消化在
 * 請求裡、使用者的分頁原地不動，兩者都到不了 Keycloak。
 *
 * CSRF token 以 `_csrf` 欄位帶上（Spring 的 CsrfFilter 同時接受請求參數與 header），
 * header 在表單送出時無從設定。
 */
export function logout(): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = logoutUrl();
  form.style.display = 'none';

  appendField(form, 'return_to', returnToParam());

  const token = readCsrfToken();
  if (token) {
    appendField(form, '_csrf', token);
  }

  document.body.appendChild(form);
  form.submit();
}

function appendField(form: HTMLFormElement, name: string, value: string): void {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value;
  form.appendChild(input);
}
